import { EyeOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Col, Empty, Row, Table, message, Tag } from "antd";
import { ColumnsType } from "antd/lib/table/Table";
import { ColumnType } from "antd/lib/table/interface";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import queryString from "query-string";
import React, { Fragment } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import { PageToolbar } from "../components/PageToolbar";
import Card from "../components/Card";
import ExportData from "../components/ExportData";
import TemplateTypeTag from "../components/TemplateTypeTag";
import { NotificationRecord, RowSelection } from "../helpers/shared-interfaces";
import {
  injectKey,
  loadSavedFilters,
  request,
  responseHandler,
  getStateColor,
  formatSnakeCase,
} from "../helpers/utilities";
import { GET_NOTIFICATION_RECORDS } from "../gql/notification-records";
import { DEBOUNCE_TIME, PAGE_SIZE } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface NotifierSearchParams {
  page: number;
  q: string;
}

const NotificationCategoryMap: Record<string, string> = {
  onboarding: "Onboarding",
  transfer_requests: "Transfer Requests",
  redemption_requests: "Redemption Requests",
  customer_interaction: "Customer Interaction",
  company_interaction: "Company Interaction",
  customer_transaction: "Online Transaction",
  opportunity: "Opportunity",
  qpr_report: "QPR Report",
  loan_request: "Loan Request",
  fixed_income_requests: "Fixed Income Requests",
  fixed_income_redemptions: "Fixed Income Redemptions",
};

interface State {
  notificationRecords: NotificationRecord[];
  isLoading: boolean;
  showDialog: boolean;
  notificationsCount: number;
  selectedTypes: string[];
  selectedCategories: string[];
  selectedStatuses: string[];
  page: number;
  pageSize: number;
  selectedIdsToExport: React.Key[];
  q: string;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

export function NotificationRecordTableFactory(
  data: NotificationRecord[],
  columns: ColumnsType<NotificationRecord>,
  isLoading?: boolean,
  page?: number,
  count?: number,
  nextPage?: (page: number, pageSize: number) => void,
): JSX.Element {
  const tempData: NotificationRecord[] = data?.map(
    (record: NotificationRecord): NotificationRecord => {
      return { ...record, key: record.id };
    },
  );

  return (
    <Table
      loading={isLoading}
      scroll={{ x: 500 }}
      columns={columns}
      dataSource={tempData}
      pagination={{
        current: page,
        defaultPageSize: PAGE_SIZE,
        total: count,
        onChange: nextPage,
      }}
    />
  );
}

export const notificationColumns = [
  {
    title: "Notification Date",
    dataIndex: "createdAt",
    key: "createdAt",
    align: "left" as const,
    render(text: string, record: NotificationRecord): JSX.Element {
      const date = new Date(record.createdAt);
      return (
        <span>
          {record.createdAt
            ? moment(date, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY HH:mm")
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Petra ID",
    dataIndex: "petraId",
    key: "petraId",
    width: "10%",
    render(text: string, record: NotificationRecord): JSX.Element {
      if (record.customer) {
        return (
          <NavLink to={`/customers/${record.customer?.id}`}>
            {record.customer?.petraId}
          </NavLink>
        );
      } else if (record.company) {
        return (
          <NavLink to={`/companies/${record.company?.id}`}>
            {record.company?.petraId}
          </NavLink>
        );
      } else if (record.trustee) {
        return (
          <NavLink to={`/trustees/${record.trustee?.id}`}>
            {record.trustee?.company?.petraId}
          </NavLink>
        );
      } else {
        return <Fragment>N/A</Fragment>;
      }
    },
  },
  {
    title: "Notification Type",
    dataIndex: "notificationType",
    key: "notificationType",
    align: "center" as const,
    render(text: string): JSX.Element {
      return <TemplateTypeTag text={text} centered />;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    align: "left" as const,
    render(text: string): JSX.Element {
      return (
        <Tag
          color={getStateColor(text)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(text)}
        </Tag>
      );
    },
  },
  {
    title: "Subject",
    dataIndex: "message",
    key: "message",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <Fragment>{text}</Fragment>;
    },
  },
  {
    title: "Recipient",
    dataIndex: "recipient",
    key: "recipient",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <Fragment>{text}</Fragment>;
    },
  },
  {
    title: "Notification",
    dataIndex: "notifierRecord",
    key: "notifierRecord",
    align: "center" as const,
    render(text: string, record: NotificationRecord): JSX.Element {
      return (
        <div style={{ textAlign: "center" }}>
          <NavLink to={`/notification-records/${record?.id}`}>
            <EyeOutlined />
          </NavLink>
        </div>
      );
    },
  },
];

export function getColumns<
  T extends NotificationRecord = NotificationRecord,
>(): ColumnType<T>[] {
  return notificationColumns.filter((col) => col.key !== "notifierRecord");
}

export const columns = notificationColumns;
export const modifiedColumns = getColumns();

export default class NotificationRecords extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      isLoading: true,
      notificationRecords: [],
      notificationsCount: 0,
      selectedCategories: [],
      selectedTypes: [],
      selectedStatuses: [],
      page: 1,
      pageSize: 25,
      selectedIdsToExport: [],
      q: "",
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
    };

    this.nextPage = this.nextPage.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
  }

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, page, selectedTypes, selectedCategories, selectedStatuses } =
      this.state;
    interface Response {
      count: number;
      results: NotificationRecord[];
    }

    this.setState({ isLoading: true });

    try {
      const r: AxiosResponse = await request({
        query: GET_NOTIFICATION_RECORDS,
        variables: {
          q,
          page,
          status:
            selectedStatuses.length > 0
              ? selectedStatuses.join(",")
              : undefined,
          notificationType:
            selectedTypes.length > 0 ? selectedTypes.join(",") : undefined,
          notificationCategory:
            selectedCategories.length > 0
              ? selectedCategories.join(",")
              : undefined,
        },
      });

      const { err, mutation } = responseHandler<Response>(r, "notifierRecords");
      if (err) {
        message.error(`Failed To Load Notifications - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          notificationsCount: mutation.count || 0,
          notificationRecords: mutation.results || [],
        });
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState({ page, pageSize: pageSize }, async () => {
      if (this.state.selectedFieldsToDisplay.length < 1) {
        await this.triggerImmediateSearch();
      }
    });
  }

  async componentDidMount(): Promise<void> {
    this.mergeWithLocalState();
    if (loadSavedFilters("notifierRecord").length < 1) {
      await this.triggerImmediateSearch();
    }
  }

  syncBrowserQueryString = (): void => {
    const { q, page, selectedStatuses, selectedCategories, selectedTypes } =
      this.state;
    const queryStrings = [];

    if (q) {
      queryStrings.push(`q=${q}`);
    }

    if (selectedStatuses.length > 0) {
      queryStrings.push(`status=${selectedStatuses.join(",")}`);
    }

    if (selectedCategories.length > 0) {
      queryStrings.push(`category=${selectedCategories.join(",")}`);
    }

    if (selectedTypes.length > 0) {
      queryStrings.push(`type=${selectedTypes.join(",")}`);
    }

    queryStrings.push(`page=${page}`);
    const queryString = `?${queryStrings.join("&")}`;
    this.props.history.replace(queryString);
  };

  extractQueryParamsFromUrl = (): NotifierSearchParams => {
    const parsed = queryString.parse(window.location.search);
    return JSON.parse(JSON.stringify(parsed)) as NotifierSearchParams;
  };

  mergeWithLocalState = (): void => {
    const parsed = this.extractQueryParamsFromUrl();
    let q = "";
    let page = 1;

    if (parsed.q) {
      q = parsed.q;
    }
    if (parsed.page) {
      page = parsed.page;
    }

    this.setState({
      q,
      page,
      selectedTypes: [],
      selectedCategories: [],
      selectedStatuses: [],
    });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ): void => {
    this.setState({
      notificationRecords: data.results as NotificationRecord[],
      selectedFieldsToDisplay: fields,
      notificationsCount: data.count,
    });
  };

  toggleAdvancedSearchModel = (): void => {
    const prevState = this.state.showAdvancedSearchModal;
    this.setState({ showAdvancedSearchModal: !prevState });
  };

  handleOnAdvancedSearchError = (error: string): void => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField): void => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState(
      {
        removedField: field,
        selectedFieldsToDisplay: fields,
      },
      () => {
        localStorage.setItem(
          "notifierRecord",
          JSON.stringify(this.state.selectedFieldsToDisplay),
        );
      },
    );

    if (fields.length === 0) this.triggerDelayedSearch();
  };

  onSelectChange(newSelectedIdsToExport: React.Key[]): void {
    this.setState({
      selectedIdsToExport: Array.from(newSelectedIdsToExport).map(
        (item) => item,
      ),
    });
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      notificationRecords,
      notificationsCount,
      page,
      pageSize,
      selectedIdsToExport,
      q,
      selectedFieldsToDisplay,
      removedField,
      selectedStatuses,
      selectedCategories,
      selectedTypes,
    } = this.state;

    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };

    return (
      <>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={this.state.showAdvancedSearchModal}
          model="notifierRecord"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
          page={page}
        />
        <Card>
          <div className="notificationRecords-table-container">
            <PageToolbar modelName="notifications"
              searchPlaceholder="Search by Petra ID, Subject"
              searchValue={q}
              onSearchChange={this.handleSearchInput}
              totalCount={notificationsCount}
              currentDataCount={notificationRecords?.length || 0}
              page={page}
              pageSize={pageSize}
              selectedCount={selectedIdsToExport.length}
              advancedSearch={{
                onOpen: this.toggleAdvancedSearchModel,
                activeFields: selectedFieldsToDisplay,
                onFieldRemove: this.handleOnFieldRemove,
              }}
              actions={
                selectedIdsToExport.length > 0 && (
                  <ExportData
                    model={"notifierRecord"}
                    dataArray={notificationRecords || []}
                    size={"large"}
                    customIds={selectedIdsToExport}
                  />
                )
              }
              filters={
                <PageToolbar.Filters
                  isFiltering={
                    !!q ||
                    selectedStatuses.length > 0 ||
                    selectedCategories.length > 0 ||
                    selectedTypes.length > 0
                  }
                  onClear={() => {
                    this.setState(
                      {
                        q: "",
                        page: 1,
                        selectedStatuses: [],
                        selectedCategories: [],
                        selectedTypes: [],
                      },
                      () => this.triggerImmediateSearch(),
                    );
                  }}
                  configs={[
                    {
                      label: "Status",
                      options: [
                        { label: "Delivered", value: "delivered" },
                        { label: "Failed", value: "failed" },
                        { label: "Pending", value: "pending" },
                        { label: "Queued", value: "queued" },
                      ],
                      selectedValues: selectedStatuses,
                      onChange: (values) => {
                        this.setState(
                          { selectedStatuses: values as string[], page: 1 },
                          () => this.triggerImmediateSearch(),
                        );
                      },
                    },
                    {
                      label: "Category",
                      options: Object.entries(NotificationCategoryMap).map(
                        ([key, value]) => ({ label: value, value: key }),
                      ),
                      selectedValues: selectedCategories,
                      onChange: (values) => {
                        this.setState(
                          { selectedCategories: values as string[], page: 1 },
                          () => this.triggerImmediateSearch(),
                        );
                      },
                    },
                    {
                      label: "Type",
                      options: [
                        { label: "Email", value: "email" },
                        { label: "SMS", value: "sms" },
                      ],
                      selectedValues: selectedTypes,
                      onChange: (values) => {
                        this.setState(
                          { selectedTypes: values as string[], page: 1 },
                          () => this.triggerImmediateSearch(),
                        );
                      },
                    },
                  ]}
                />
              }
            />
            <Row>
              <Col span={24}>
                {(notificationRecords || []).length === 0 ? (
                  <Empty />
                ) : (
                  <Table
                    loading={isLoading}
                    scroll={{ x: 500 }}
                    bordered={true}
                    columns={notificationColumns}
                    dataSource={injectKey<NotificationRecord>(
                      notificationRecords || [],
                      "id",
                    )}
                    onRow={(record: NotificationRecord) => ({
                      onClick: () => {
                        this.props.history.push({
                          pathname: `/notification-records/${record.id}`,
                          state: {
                            type: "notificationRecord",
                            notificationRecords: notificationRecords || [],
                          },
                        });
                      },
                      style: { cursor: "pointer" },
                    })}
                    pagination={{
                      current: page,
                      pageSize: pageSize,
                      total: notificationsCount,
                      onChange: this.nextPage,
                      showSizeChanger: true,
                    }}
                    rowSelection={rowSelection}
                  />
                )}
              </Col>
            </Row>
          </div>
        </Card>
      </>
    );
  }
}
